import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useClassy from '@core/hooks/useClassy';
import { useProjectStore, useSuperHubStore } from '@core/store';
import { isBranch } from '@core/store/SuperHub/util';
import cleanBranchName from '@core/utils/i18n/cleanBranchName';
import useBuildBranchPath from '@routes/SuperHub/Versions/hooks/useBuildBranchPath';
import Badge from '@ui/Badge';
import Button from '@ui/Button';
import Dropdown from '@ui/Dropdown';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Menu, { MenuItem } from '@ui/Menu';
import Tooltip from '@ui/Tooltip';
import Truncate from '@ui/Truncate';

import styles from './index.module.scss';

function VersionDropdown() {
  const { t } = useTranslation();
  const bem = useClassy(styles, 'VersionDropdown');
  const [currentBranch, versions, i18nEnabled] = useSuperHubStore(s => [
    s.currentBranch,
    s.versions.data.data,
    s.isI18nEnabled,
  ]);
  const versionVisibility = useProjectStore(s => s.data.appearance.navigation.version);
  const buildVersionPath = useBuildBranchPath();

  const currentVersion = useMemo(() => {
    return isBranch(currentBranch) ? currentBranch.base : currentBranch;
  }, [currentBranch]);

  // Render nothing if for some reason no current version exists.
  if (!currentVersion) return null;

  // Render nothing if versions are configured to be hidden in the subnav.
  if (versionVisibility === 'disabled') return null;

  const currentVersionLabel =
    currentVersion?.display_name || `v${i18nEnabled ? cleanBranchName(currentVersion.name) : currentVersion.name}`;
  const currentLangFilter = currentVersion.i18n.lang;

  // When only a single version exists, render a static label.
  if (versions.length <= 1) {
    return (
      <Truncate className={bem('&', '_inactive', 'rm-Header-bottom-link')} maxWidth="150px">
        {currentVersionLabel}
      </Truncate>
    );
  }

  return (
    <Dropdown appendTo={() => document.body} className="rm-VersionDropdown" justify="start" sticky>
      <Button
        aria-label="Toggle version dropdown"
        bem={{ slate_text: true }}
        className={bem('&', 'rm-Header-bottom-link')}
      >
        <Truncate maxWidth="150px">{currentVersionLabel}</Truncate>
        <Icon name="chevron-down" />
      </Button>
      <Menu className="rm-VersionDropdown-menu">
        {versions.map(version => {
          const versionLabel = version.display_name || `v${i18nEnabled ? cleanBranchName(version.name) : version.name}`;

          // ensure versions are filtered by lang on the client
          if (i18nEnabled && version.i18n.lang !== currentLangFilter) return null;

          return (
            <MenuItem
              key={version.name}
              active={currentVersion.name === version.name}
              href={buildVersionPath(version.name)}
              TagName="a"
            >
              <Flex className={bem('-item')} gap="0" layout="col">
                <Flex align="center" className={bem('-item-content')} gap="xs">
                  <span className={bem('-item-label')}>{versionLabel}</span>
                  {version.privacy.view === 'default' && <Badge kind="success">{t('version.default')}</Badge>}
                  {version.state === 'deprecated' && <Badge>{t('version.deprecated')}</Badge>}
                  {version.release_stage === 'beta' && <Badge>{t('version.beta')}</Badge>}
                  {version.privacy.view === 'hidden' && (
                    <Tooltip content={t('onlyVisibleToAdmins')} placement="bottom">
                      <Icon aria-label={t('version.hiddenLabel')} name="eye-off" />
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            </MenuItem>
          );
        })}
      </Menu>
    </Dropdown>
  );
}

export default VersionDropdown;
